import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import { TOUR_AI_STATUS_LINES } from "./tourData";

function TourFillMagicProgress({ statusLines = TOUR_AI_STATUS_LINES }) {
  const [statusIndex, setStatusIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % statusLines.length);
    }, 900);
    return () => window.clearInterval(id);
  }, [statusLines]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      className="relative mr-2 overflow-hidden rounded-xl border border-primary/20 bg-background px-3 py-3 shadow-sm"
    >
      <motion.div
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent"
        animate={{ x: ["-120%", "120%"] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden
      />
      <div className="relative z-[1] flex items-start gap-3">
        <div className="relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center">
          <motion.span
            className="absolute inset-0 rounded-full bg-primary/15"
            animate={{ scale: [1, 1.25, 1], opacity: [0.55, 0.2, 0.55] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden
          />
          <motion.span
            animate={{ rotate: [0, 12, -8, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          >
            <Icon name="Sparkles" size={18} className="text-primary" />
          </motion.span>
        </div>
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="min-h-[1.25rem]">
            <AnimatePresence mode="wait">
              <motion.p
                key={statusIndex}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22 }}
                className="text-sm font-medium tracking-tight text-foreground"
              >
                {statusLines[statusIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <motion.div
              className="h-full rounded-full bg-primary"
              initial={{ width: "10%" }}
              animate={{ width: ["14%", "48%", "72%", "90%"] }}
              transition={{ duration: 2.4, ease: "easeInOut" }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function TourSpinnerProgress({ label = "Processando seu pedido…" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="mr-2 flex items-center gap-2.5 rounded-lg border border-border bg-background/95 px-3 py-2 text-sm text-muted-foreground"
    >
      <span
        className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-muted border-t-primary"
        aria-hidden
      />
      <span className="tracking-tight">{label}</span>
    </motion.div>
  );
}

/**
 * Read-only NeoAi dock for tour scenes — mirrors production chat chrome.
 */
const TourNeoAiDock = ({
  messages,
  loading,
  composerText,
  placeholder = "Ex.: Elabore um contrato de prestação de serviços…",
  loadingVariant = "spinner",
  statusLines,
  loadingLabel,
  className,
}) => {
  return (
    <div
      className={cn(
        "flex w-full max-w-[22.5rem] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-elevation-lg",
        className
      )}
    >
      <div className="flex shrink-0 items-center justify-between gap-2 bg-primary px-3 py-2.5 text-primary-foreground">
        <span className="flex items-center gap-2 text-sm font-semibold tracking-tight">
          <Icon name="Sparkles" size={18} />
          NeoAi
        </span>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-md bg-primary-foreground/15 px-2 py-1 text-xs font-medium tabular-nums">
            <Icon name="Coins" size={14} aria-hidden />
            120
          </span>
          <span className="rounded-md p-1.5 opacity-80">
            <Icon name="X" size={18} />
          </span>
        </div>
      </div>

      <div className="min-h-[180px] max-h-[min(44vh,19rem)] flex-1 space-y-3 overflow-y-auto bg-muted/20 px-3 py-2">
        <AnimatePresence initial={false}>
          {messages.map((message, index) => (
            <motion.div
              key={`${message.role}-${index}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                "rounded-lg px-3 py-2 text-sm leading-relaxed",
                message.role === "user"
                  ? "ml-4 bg-primary/10 text-foreground"
                  : "mr-2 border border-border bg-background"
              )}
            >
              <span className="whitespace-pre-wrap">{message.text}</span>
            </motion.div>
          ))}
        </AnimatePresence>
        <AnimatePresence mode="wait">
          {loading ? (
            loadingVariant === "magic" ? (
              <TourFillMagicProgress key="magic" statusLines={statusLines} />
            ) : (
              <TourSpinnerProgress key="spinner" label={loadingLabel} />
            )
          ) : null}
        </AnimatePresence>
      </div>

      <div className="shrink-0 border-t border-border bg-card p-2">
        <div className="min-h-[2.75rem] rounded-lg border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
          {composerText || <span className="opacity-60">{placeholder}</span>}
        </div>
      </div>
    </div>
  );
};

export default TourNeoAiDock;
